import { useCallback, useRef } from 'react';
import type {
  ReactElement,
  PropsWithChildren,
  KeyboardEventHandler,
  MutableRefObject,
} from 'react';

import * as styles from './Option.module.scss';

export interface OptionProps<T> {
  isSelected?: boolean;
  value: T;
  title?: string;
  onOptionClick: (param: {
    value: T;
    node: MutableRefObject<HTMLLIElement>;
  }) => void;
}

export const Option = <T extends string | number>({
  children,
  isSelected = false,
  value,
  title,
  onOptionClick,
}: PropsWithChildren<OptionProps<T>>): ReactElement | null => {
  const ref = useRef<HTMLLIElement>(null!);

  const handleOptionClick = useCallback(() => {
    onOptionClick({ value, node: ref });
  }, [onOptionClick, value]);

  const handleKeyDown: KeyboardEventHandler<HTMLLIElement> = useCallback(
    (e) => {
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();

        handleOptionClick();
      }
    },
    [handleOptionClick]
  );

  return (
    <li
      ref={ref}
      aria-selected={isSelected}
      tabIndex={0}
      role="option"
      className={styles.option}
      onClick={handleOptionClick}
      onKeyDown={handleKeyDown}
      title={title}
    >
      {children}
    </li>
  );
};
